import styled, { css, keyframes } from 'styled-components';
import { getStyle as get } from '@ids/ddms-styled/comps/dialog/intuit/light';
import { getStyle as getLgStyle } from '@ids/ddms-styled/comps/dialogLg/intuit/light';
import Button from '@ids/button';
import { CloseIconControl } from '@ids/icon-control';
import { transitionState } from '@ids/transition';

// For Modal.js
const fadeEnter = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeExit = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const growEnter = keyframes`
  from {
    -webkit-transform: scale(0.8);
    transform: scale(0.8);
  }
  to {
    -webkit-transform: none;
    transform: none;
  }
`;

const shrinkSubtleExit = keyframes`
  from {
    -webkit-transform: none;
    transform: none;
  }
  to {
    -webkit-transform: scale(0.9);
    transform: scale(0.9);
  }
`;

const transitionEnter = css`
  -webkit-animation: ${fadeEnter} 250ms cubic-bezier(0.35, 0, 0.5, 1) backwards,
    ${growEnter} 750ms cubic-bezier(0.15, 1.3, 0.3, 1) backwards;
  animation: ${fadeEnter} 250ms cubic-bezier(0.35, 0, 0.5, 1) backwards,
    ${growEnter} 750ms cubic-bezier(0.15, 1.3, 0.3, 1) backwards;
`;

const transitionExit = css`
  -webkit-animation: ${fadeExit} 250ms cubic-bezier(0.35, 0, 0.5, 1) forwards,
    ${shrinkSubtleExit} 250ms cubic-bezier(0.35, 0, 0.8, 1) forwards;
  animation: ${fadeExit} 250ms cubic-bezier(0.35, 0, 0.5, 1) forwards,
    ${shrinkSubtleExit} 250ms cubic-bezier(0.35, 0, 0.8, 1) forwards;
`;

const transitionStatusMap = {
  [transitionState.ENTERING]: transitionEnter,
  [transitionState.EXITING]: transitionExit
};

// No design data available for different size widths
const sizeMap = {
  small: '384px',
  medium: '567px',
  large: '752px'
};

export const Wrapper = styled.div`
  background-color: ${getLgStyle('background.color')};
  border: ${getLgStyle('background.border')} solid #d4d7dc;
  border-radius: ${props => (props.theme === 'quickbooks' ? '8px' : '12px')};
  box-shadow: 0 4px 16px 0 rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  max-height: 680px;
  overflow: hidden;
  margin: 0 auto;
  z-index: 3;
  width: ${({ size }) => sizeMap[size]};

  &:focus {
    outline: none;
  }

  @media only screen and (max-width: 479px) {
    width: calc(100vw - 32px);
    max-height: calc(100% - 32px);
    margin: 16px;
  }

  @media only screen and (max-height: 520px) and (orientation: landscape) {
    overflow: auto;
    justify-content: flex-start;
  }
`;

export const Container = styled.div`
  position: fixed;
  z-index: 2;
  left: 0;
  top: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow: auto;
  margin: 0 auto;

  ${props => transitionStatusMap[props.transitionState]}
`;

export const StyledCloseIcon = styled(CloseIconControl)`
  margin: 20px 20px 0 0;
  position: relative;
  float: right;
  @media only screen and (max-width: 479px) {
    margin: 16px 16px 0 0;
  }
`;

// For ModalActions.js
const actionsAlignment = {
  center: 'center',
  right: 'right'
};

export const ActionsWrapper = styled.div`
  order: 1;
  flex: 1 1 auto;
  margin: 0 20px 20px;
  padding-top: ${({ sectionDivider }) => (sectionDivider ? '0px' : '20px')};
  padding-right: 20px;
  padding-bottom: 20px;
  padding-left: 20px;
  overflow: hidden;
  text-align: ${({ alignment }) => actionsAlignment[alignment]};
  @media only screen and (max-width: 479px) {
    padding: 0 16px 16px;
    margin: 0 auto;
  }
`;

export const ActionsSectionDivider = styled.hr`
  border-top: 1px solid ${get('divider.color')};
  display: block;
  margin: ${get('divider.spacingBottom.normal')} 0;
  overflow: hidden;
  @media only screen and (max-width: 479px) {
    margin: ${get('divider.spacingBottom.responsiveXS')} 0;
  }
`;

export const ButtonSection = styled.div`
  padding-top: ${({ sectionDivider }) => (sectionDivider ? '20px' : '0px')};
  @media only screen and (max-width: 479px) {
    padding: 0;
  }
`;

export const StyledButton = styled(Button)`
  display: inline-block;
  margin-left: ${({ buttonCount, buttonType }) =>
    buttonCount !== 1 && buttonType === 'primary' ? '16px' : ''};
  float: ${({ buttonCount, buttonType, hasDismiss }) =>
    hasDismiss && buttonCount !== 1 && buttonType === 'primary' ? 'right' : ''};
  float: ${({ buttonCount, buttonType }) =>
    buttonCount !== 1 && buttonType === 'tertiary' ? 'left' : ''};
  @media only screen and (max-width: 1023px) {
    width: 100%;
    float: none;
    margin: 16px 0 0;
  }
`;

// For ModalContent.js
const contentAlignment = {
  center: 'center',
  left: 'left'
};

export const ContentWrapper = styled.div`
  position: relative;
  display: inline-flex;
  flex: 1 1 auto;
  width: 100%;

  ${props =>
    props.overflow &&
    css`
      &:after {
        background: linear-gradient(
          to bottom,
          rgba(255, 255, 255, 0),
          rgba(255, 255, 255, 1)
        );
        bottom: 0;
        content: ' ';
        height: 56px;
        left: 0;
        position: absolute;
        width: 100%;
        pointer-events: none;
      }
    `};

  @media only screen and (max-width: 320px) {
    max-height: 150px;
  }

  @media only screen and (max-width: 479px) {
    max-height: 200px;
  }

  @media only screen and (max-height: 520px) and (orientation: landscape) {
    ${props =>
      props.overflow &&
      css`
        &:after {
          display: none;
        }
      `};
  }
`;

export const InnerContainer = styled.div`
  color: ${get('contentSection.fontcolor')};
  font-style: ${get('contentSection.fontStyle')};
  font-weight: ${get('contentSection.fontWeight')};
  font-size: ${get('contentSection.fontSize.normal')};
  line-height: ${get('contentSection.lineHeight.normal')};
  ${({ overflow }) => (overflow ? 'overflow-y: auto; max-height: 300px;' : '')}
  max-height: ${({ maxHeight }) => maxHeight};
  margin: 0 20px;
  padding: 20px;
  position: relative;
  width: 100%;
  outline: none;
  text-align: ${({ alignment }) => contentAlignment[alignment]};
  @media only screen and (max-height: 520px) and (orientation: landscape) {
    ${({ overflow }) =>
      overflow ? 'overflow-y: hidden; max-height: fit-content;' : ''}
  }

  @media only screen and (max-width: 479px) {
    font-size: ${get('contentSection.fontSize.ResponsiveXS')};
    line-height: ${get('contentSection.lineHeight.ResponsiveXS')};
    margin: 0 16px;
    padding: 16px;
  }
`;

// for ModalHeader.js
const headerTextAlign = {
  center: 'center',
  left: 'left'
};

export const HeaderWrapper = styled.header`
  background-color: ${({ transparentBackground }) =>
    transparentBackground ? 'transparent' : '#EBF9FF'};
  padding-top: 20px;
  padding-left: 20px;
  padding-right: 20px;
  padding-bottom: ${({ transparentBackground }) =>
    transparentBackground ? '20px' : '40px'};
  text-align: ${({ alignment }) => headerTextAlign[alignment]};
  @media only screen and (max-width: 767px) {
    padding-top: 20px;
    padding-left: 16px;
    padding-right: 16px;
    padding-bottom: 16px;
  }
`;

// for ModalTitle.js
export const Title = styled.h3`
  font-style: normal;
  font-weight: 500;
  font-size: 30px;
  line-height: 32px;
  color: #393a3d;
  margin: 20px 20px 0;
  @media only screen and (max-width: 479px) {
    margin: 16px 16px 0;
    font-size: 24px;
    line-height: 28px;
  }
`;

export const SubTitle = styled.p`
  color: ${get('subHeadline.color')};
  font-style: ${get('subHeadline.fontStyle')};
  font-weight: ${get('subHeadline.fontWeight')};
  font-size: 19px;
  line-height: 24px;
  padding-top: ${get('subHeadline.spacingTop')};
  margin: 0 20px;
  @media only screen and (max-width: 479px) {
    margin: 0 16px;
  }
`;
